import chroma from 'chroma-js';

const THEMES = {
  cyberpunk: {
    colors: ['#0ff0fc', '#ff00ff', '#8000ff', '#ff0055', '#1a1a1a'],
    keywords: ['neon', 'future', 'night', 'glitch', 'techno', 'dark', 'glow']
  },
  autumn: {
    colors: ['#8b4513', '#d2691e', '#ff8c00', '#daa520', '#556b2f'],
    keywords: ['fall', 'leaves', 'warm', 'forest', 'harvest', 'gold', 'brown']
  },
  ocean: {
    colors: ['#0077be', '#00a8e8', '#48cae4', '#ade8f4', '#023e8a'],
    keywords: ['sea', 'water', 'blue', 'deep', 'beach', 'waves', 'marine']
  },
  sunset: {
    colors: ['#ff512f', '#dd2476', '#ffaf7b', '#f8d800', '#642b73'],
    keywords: ['evening', 'sky', 'warm', 'dusk', 'horizon', 'pink', 'orange']
  },
  forest: {
    colors: ['#1b4332', '#2d6a4f', '#40916c', '#52b788', '#b7e4c7'],
    keywords: ['nature', 'green', 'wood', 'jungle', 'leaf', 'tree', 'moss']
  },
  minimalist: {
    colors: ['#f8f9fa', '#e9ecef', '#dee2e6', '#ced4da', '#adb5bd'],
    keywords: ['clean', 'modern', 'simple', 'white', 'grey', 'light', 'zen']
  },
  pastel: {
    colors: ['#ffb7b2', '#ffdac1', '#e2f0cb', '#b5ead7', '#c7ceea'],
    keywords: ['soft', 'cute', 'candy', 'spring', 'light', 'gentle', 'pale']
  },
  vintage: {
    colors: ['#8d99ae', '#ef233c', '#d90429', '#2b2d42', '#edf2f4'],
    keywords: ['retro', 'old', 'classic', 'nostalgia', 'heritage', 'antique']
  },
  luxury: {
    colors: ['#000000', '#1a1a1a', '#c5a059', '#d4af37', '#e5e5e5'],
    keywords: ['gold', 'black', 'premium', 'elegant', 'rich', 'expensive', 'classy']
  },
  earthy: {
    colors: ['#6b705c', '#a5a58d', '#b7b7a4', '#ffe8d6', '#ddbea9'],
    keywords: ['stone', 'clay', 'organic', 'soil', 'natural', 'muted', 'neutral']
  }
};

/**
 * Generates a palette based on a semantic prompt.
 * @param {string} prompt - The user's input prompt.
 * @returns {Array} Array of 5 color objects {name, color}.
 */
export function generatePaletteFromPrompt(prompt) {
  const p = prompt.toLowerCase();
  let selectedTheme = null;
  let maxScore = -1;

  // Simple keyword matching for theme selection
  for (const [themeName, themeData] of Object.entries(THEMES)) {
    let score = 0;
    if (p.includes(themeName)) score += 5;

    themeData.keywords.forEach(keyword => {
      if (p.includes(keyword)) score += 1;
    });

    if (score > maxScore) {
      maxScore = score;
      selectedTheme = themeData;
    }
  }

  // If no strong match, use a fallback or generate based on basic color words
  if (maxScore <= 0) {
    return generateHeuristicPalette(p);
  }

  // Add some slight randomness to the theme colors to make it feel "AI generated"
  return selectedTheme.colors.map((hex, i) => ({
    name: `${prompt} ${i + 1}`,
    color: randomizeColor(hex)
  }));
}

/**
 * Fallback heuristic generator based on basic color mentions
 */
function generateHeuristicPalette(prompt) {
  const baseColors = [];
  if (prompt.includes('red')) baseColors.push('#ff0000');
  if (prompt.includes('blue')) baseColors.push('#0000ff');
  if (prompt.includes('green')) baseColors.push('#00ff00');
  if (prompt.includes('yellow')) baseColors.push('#ffff00');
  if (prompt.includes('purple')) baseColors.push('#800080');
  if (prompt.includes('orange')) baseColors.push('#ffa500');
  if (prompt.includes('pink')) baseColors.push('#ffc0cb');

  if (baseColors.length === 0) {
    const scale = chroma.scale(['#64B5F6', '#764ba2', '#ff0055']).colors(5);
    return scale.map((c, i) => ({
      name: `AI Color ${i + 1}`,
      color: c
    }));
  }

  // Generate a palette around found base colors
  const finalColors = [];
  while (finalColors.length < 5) {
    const base = baseColors[finalColors.length % baseColors.length];
    finalColors.push({
      name: `AI ${prompt} ${finalColors.length + 1}`,
      color: randomizeColor(base)
    });
  }
  return finalColors;
}

function randomizeColor(hex) {
  const c = chroma(hex);
  return c
    .set('hsl.h', c.get('hsl.h') + (Math.random() * 20 - 10))
    .set('hsl.s', Math.max(0, Math.min(1, c.get('hsl.s') + (Math.random() * 0.2 - 0.1))))
    .set('hsl.l', Math.max(0, Math.min(1, c.get('hsl.l') + (Math.random() * 0.2 - 0.1))))
    .hex();
}

/**
 * Suggests a minimal shift to a color to make it pass WCAG AA with a background.
 * @param {string} foreground - Hex color
 * @param {string} background - Hex color
 * @returns {string} New compliant hex color
 */
/**
 * Generates an AI-optimized modern palette variation from a base color.
 * @param {string} baseColor - Seed hex color.
 * @param {string} type - 'analogous', 'triadic', 'monochrome', 'complementary'
 * @returns {Array} Array of 5 color objects.
 */
export function generateAIVariation(baseColor, type) {
  const base = chroma(baseColor);
  let colors = [];

  switch (type) {
    case 'analogous':
      // Modern analogous: slightly shifted saturation and lightness for more pop
      colors = [
        base.set('hsl.h', '-30').set('hsl.s', '+0.1').set('hsl.l', '-0.05'),
        base.set('hsl.h', '-15').set('hsl.l', '+0.05'),
        base,
        base.set('hsl.h', '+15').set('hsl.l', '+0.05'),
        base.set('hsl.h', '+30').set('hsl.s', '+0.1').set('hsl.l', '-0.05'),
      ];
      break;
    case 'triadic':
      // Vibrant triadic: high contrast and saturation
      colors = [
        base,
        base.set('hsl.h', '+120').brighten(0.5),
        base.set('hsl.h', '+120').darken(0.5),
        base.set('hsl.h', '+240').brighten(0.5),
        base.set('hsl.h', '+240').darken(0.5),
      ];
      break;
    case 'monochrome':
      // Smooth monochrome: optimized for UI gradients
      colors = [
        base.brighten(2),
        base.brighten(1),
        base,
        base.darken(1),
        base.darken(2),
      ];
      break;
    default:
      // Complementary pop
      colors = [
        base,
        base.set('hsl.s', '-0.2'),
        base.set('hsl.h', '+180').set('hsl.s', '+0.2'),
        base.set('hsl.h', '+180'),
        base.set('hsl.h', '+180').darken(1),
      ];
  }

  return colors.map((c, i) => ({
    name: `AI ${type} ${i + 1}`,
    color: c.hex()
  }));
}

export function suggestCompliantColor(foreground, background) {
  let fg = chroma(foreground);
  let bg = chroma(background);
  let contrast = chroma.contrast(fg, bg);

  if (contrast >= 4.5) return foreground;

  // Try darkening or lightening based on background luminance
  const isBgDark = bg.luminance() < 0.5;
  let step = 0.05;
  let attempts = 0;

  while (contrast < 4.5 && attempts < 20) {
    if (isBgDark) {
      fg = fg.brighten(step);
    } else {
      fg = fg.darken(step);
    }
    contrast = chroma.contrast(fg, bg);
    attempts++;
  }

  return fg.hex();
}
